import Routine, { RUN } from "+/Engine/Routine";
import DataTypes from "+/Engine/Helpers/DataTypes";

export default class UpdateEntities extends Routine {

  assertValidParams(params) {
    DataTypes.assertObjectKeyExistsOfType("entityType", params, DataTypes.assertString);
    DataTypes.assertObjectKeyExistsOfType("whereCriteria", params, DataTypes.assertObject);
    DataTypes.assertObjectKeyExistsOfType("fields", params, DataTypes.assertObject);

    Object.keys(params.whereCriteria).forEach(fieldName => {
      const fieldDef = this.app.getEntityFieldDef(params.entityType, fieldName);
      if (fieldDef.type === "manyEntities") {
        throw new Error(`whereCriteria field "${fieldName}" is of type manyEntities which is not supported`);
      }
      if (fieldDef.type === "oneEntity" && (fieldDef.remoteLinkingField || fieldDef.remoteCurrentUserField)) {
        throw new Error(
          `whereCriteria field "${fieldName}" is a oneEntity field with `
          + "remoteLinkingField and remoteCurrentUserField which is not supported",
        );
      }
    });
  }

  [RUN](params) {
    const { entityType, whereCriteria, fields } = params;

    const allEntities = this.app.getAllEntitiesOfType(entityType);

    const matchingEntities = allEntities.filter(entity => {
      return Object.entries(whereCriteria).every(([fieldName, criterion]) => {
        const entityValue = entity[fieldName];
        return this.#evaluateCriterion(entityValue, criterion);
      });
    });

    matchingEntities.forEach(entity => {
      const fieldsToUpdate = {};
      Object.entries(fields).forEach(([fieldName, fieldDef]) => {
        if (fieldDef?.isTransformField) {
          // Apply the transform to the entity's current field value.
          const currentValue = entity[fieldName];
          fieldsToUpdate[fieldName] = Routine.applyTransform(currentValue, fieldDef.transform, {});
        } else {
          fieldsToUpdate[fieldName] = fieldDef;
        }
      });
      this.app.updateSpecificEntityFields(entityType, entity.getPk(), fieldsToUpdate);
    });
  }

  // Normalizes both sides of a comparison so oneEntity fields compare by PK value, not
  // by object identity. Without this, whereCriteria on fields like `parentElement` compare
  // two RelatedEntity instances with `==` — always false even when they refer to the same
  // parent. Shift routines would then match nothing and siblings' indexInParent would
  // drift into duplicates after repeated optimistic moves. Matches server PHP's behavior
  // in BaseEntity::buildWherePartsAndParams which extracts the PK from oneEntity values.
  #normalizeForComparison(v) {
    if (v === null || v === undefined) {
      return v;
    }
    // RelatedEntity instance: { entityType, pk: { id: X } }
    if (typeof v === "object" && v.pk && typeof v.pk === "object") {
      return JSON.stringify(v.pk);
    }
    // Plain PK object: { id: X } (what the server serializes)
    if (typeof v === "object" && !Array.isArray(v)) {
      return JSON.stringify(v);
    }
    return v;
  }

  #evaluateCriterion(entityValue, criterion) {
    const a = this.#normalizeForComparison(entityValue);
    const b = this.#normalizeForComparison(criterion.value);
    switch (criterion.operator) {
      case "equals": return a === b;
      case "greaterThan": return Number(a) > Number(b);
      case "lessThan": return Number(a) < Number(b);
      default:
        throw new Error(`unknown whereCriteria operator "${criterion.operator}"`);
    }
  }
}

Routine.register("UpdateEntities", UpdateEntities);
